import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Collapse,
  Card,
  CardBody,
  Button
} from 'reactstrap';
import numeral from 'numeral';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faSpinner,
  faUsers,
  faYenSign,
  faStoreAlt,
  faShoppingCart
} from '@fortawesome/free-solid-svg-icons';

import Map from './prefectures/Map.js';
import Search from './prefectures/Search.js';
import MetricCard from './prefecture/components/MetricCard.js';
import HistoryControls from './shared/HistoryControls.js';
import { getLatestYear, getMaxHistoryLength } from './shared/historyUtils.js';

const buildCollapsedSections = () => ({
  Demographic: true,
  Economic: true,
  Consumption: true,
  Retail: true,
  Note: true
});

class Prefectures extends Component {
  constructor(props) {
    super(props);

    const historyProvince = window.history.state ? window.history.state.province : null;
    const historyPrefecture = window.history.state ? window.history.state.prefecture : null;

    this.state = {
      province: historyProvince || null,
      prefecture: historyPrefecture || null,
      dataPointNames: [],
      provinceStats: null,
      provinceStatsLoading: false,
      prefectureStats: null,
      prefectureStatsLoading: false,
      historyWindow: 'recent',
      collapsedSections: buildCollapsedSections()
    };
  }

  componentDidMount() {
    const { comparablePrefectureId } = this.props;

    fetch('/api/data_exploration/getAllDataPointNames').then((resp) => resp.json())
      .then((respJson) => {
        this.setState({ dataPointNames: respJson });
      });

    if(this.state.prefecture && !comparablePrefectureId) {
      this.loadPrefectureStats(this.state.prefecture);
    } else if(this.state.province && !comparablePrefectureId) {
      this.loadProvinceStats(this.state.province);
    }

    if(comparablePrefectureId) {
      fetch('/api/prefectures/' + comparablePrefectureId).then((resp) => resp.json())
        .then((respJson) => {
          const prefecture = respJson.data;
          if(prefecture && prefecture.relationships) {
            fetch(prefecture.relationships.province.links.related).then((resp) => resp.json())
              .then((respData) => {
                this.setState({
                  province: respData.data,
                  prefecture: prefecture
                });
              });
          }
        });
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if(prevState.prefecture !== this.state.prefecture) {
      if(this.state.prefecture) {
        this.setState({
          historyWindow: 'recent',
          collapsedSections: buildCollapsedSections(),
          provinceStats: null,
          provinceStatsLoading: false
        });
        this.loadPrefectureStats(this.state.prefecture);
      } else {
        this.setState({
          prefectureStats: null,
          prefectureStatsLoading: false
        });

        if(this.state.province) {
          this.loadProvinceStats(this.state.province);
        }
      }
    }

    if(prevState.province !== this.state.province && !this.state.prefecture) {
      if(this.state.province) {
        this.setState({
          historyWindow: 'recent',
          collapsedSections: buildCollapsedSections()
        });
        this.loadProvinceStats(this.state.province);
      } else {
        this.setState({
          provinceStats: null,
          provinceStatsLoading: false
        });
      }
    }
  }

  getStatsLink(entity) {
    return entity && entity.relationships && entity.relationships['year-stats']
      ? entity.relationships['year-stats'].links.related
      : null;
  }

  loadProvinceStats(province) {
    const statsLink = this.getStatsLink(province);
    if(!statsLink || !province.relationships['year-stats'].data.length) {
      this.setState({ provinceStats: null, provinceStatsLoading: false });
      return;
    }

    this.setState({ provinceStatsLoading: true, provinceStats: null });
    this.fetchStats(statsLink, (stats) => {
      this.setState({ provinceStats: stats, provinceStatsLoading: false });
    });
  }

  loadPrefectureStats(prefecture) {
    const statsLink = this.getStatsLink(prefecture);
    if(!statsLink || !prefecture.relationships['year-stats'].data.length) {
      this.setState({ prefectureStats: null, prefectureStatsLoading: false });
      return;
    }

    this.setState({ prefectureStatsLoading: true, prefectureStats: null });
    this.fetchStats(statsLink, (stats) => {
      this.setState({ prefectureStats: stats, prefectureStatsLoading: false });
    });
  }

  fetchStats(link, onComplete, collected = []) {
    fetch(link + (link.includes('page[size]=') ? '' : '&page[size]=50')).then((resp) => resp.json())
      .then((respJson) => {
        const combined = collected.concat(respJson.data || []);
        if(respJson.links && respJson.links.next) {
          const linkPieces = respJson.links.next.split('?');
          const formattedLink = linkPieces.length === 2
            ? linkPieces.join('?')
            : linkPieces[0] + '?' + linkPieces[2];
          this.fetchStats(formattedLink, onComplete, combined);
        } else {
          onComplete(this.formatData(combined));
        }
      });
  }

  formatData(rawData) {
    const data = {};
    (rawData || []).forEach((item) => {
      let value = numeral(item.attributes.value * item.attributes.multiplier).format('0,0');
      if(item.attributes.unit === '%') {
        value += item.attributes.unit;
      } else if(item.attributes.unit) {
        value += ' ' + item.attributes.unit;
      }

      if(data[item.attributes['data-point-name']]) {
        data[item.attributes['data-point-name']][item.attributes.year] = value;
      } else {
        data[item.attributes['data-point-name']] = {};
        data[item.attributes['data-point-name']][item.attributes.year] = value;
      }
    });
    return data;
  }

  getLatestPoint(stats, dataPointName) {
    if(stats && stats[dataPointName]) {
      const latestYear = getLatestYear(stats[dataPointName]);
      return {
        year: latestYear,
        value: stats[dataPointName][latestYear]
      };
    }

    return null;
  }

  getCategoryMetrics(stats, category) {
    const { dataPointNames } = this.state;
    if(!stats) return [];

    return dataPointNames
      .filter((dp) => dp.category === category && stats[dp.name])
      .map((dp) => ({
        name: dp.title || dp.name,
        yearData: stats[dp.name]
      }));
  }

  toggleSection(sectionName) {
    this.setState((prevState) => ({
      collapsedSections: {
        ...prevState.collapsedSections,
        [sectionName]: !prevState.collapsedSections[sectionName]
      }
    }));
  }

  renderFullWidthSection(sectionName, title, description, icon, metrics, loading) {
    const { historyWindow, collapsedSections } = this.state;
    const collapsed = collapsedSections[sectionName];

    return (
      <div key={sectionName} className="data-section mb-3">
        <div className="data-section-header" onClick={() => this.toggleSection(sectionName)}>
          <div>
            <div className="data-section-title">
              <FontAwesomeIcon icon={icon} />
              {title}
            </div>
            <div className="data-section-subtitle">{description}</div>
          </div>
          <div className="data-section-header-right">
            <span className="data-section-meta">{metrics.length} metrics · {getMaxHistoryLength(metrics)} years</span>
            <FontAwesomeIcon icon={collapsed ? faChevronDown : faChevronUp} className="text-muted" />
          </div>
        </div>
        <Collapse isOpen={!collapsed}>
          {loading ? (
            <div className="text-center py-4">
              <FontAwesomeIcon icon={faSpinner} size="3x" spin className="text-primary" />
            </div>
          ) : metrics.length > 0 ? (
            <div className="metric-card-grid">
              {metrics.map((metric, i) => (
                <MetricCard
                  key={sectionName + '-' + i}
                  name={metric.name}
                  yearData={metric.yearData}
                  historyWindow={historyWindow} />
              ))}
            </div>
          ) : (
            <p className="text-muted small p-3 mb-0">No data available.</p>
          )}
        </Collapse>
      </div>
    );
  }

  updatePrefectures(newState) {
    this.setState(newState);
  }

  handleExploreClick() {
    const { prefecture, province } = this.state;
    if(!prefecture) return;

    window.history.replaceState({ province, prefecture }, '', window.location.href);
    window.location.href = '/data_exploration/prefecture/' + prefecture.id;
  }

  renderMarketRead() {
    const {
      province,
      prefecture,
      provinceStats,
      provinceStatsLoading,
      prefectureStats,
      prefectureStatsLoading,
      historyWindow
    } = this.state;

    const loading = prefecture ? prefectureStatsLoading : provinceStatsLoading;
    const stats = prefecture ? prefectureStats : provinceStats;
    const locationName = prefecture ? prefecture.attributes.name : province ? province.attributes.name : 'No market selected';
    const title = prefecture ? locationName + ' performance snapshot' : province ? locationName + ' performance snapshot' : 'Choose a market to begin';
    const availableYears = stats ? getMaxHistoryLength(Object.keys(stats).map((key) => ({ yearData: stats[key] }))) : 0;
    const overviewItems = prefecture
      ? [
        { label: 'Population', point: this.getLatestPoint(stats, 'Total Population'), sublabel: 'Latest population scale' },
        { label: 'GDP', point: this.getLatestPoint(stats, 'GDP'), sublabel: 'Latest market output' },
        { label: 'Per Cap GDP', point: this.getLatestPoint(stats, 'Per Cap GDP'), sublabel: 'Latest output per person' },
        { label: 'Retail Sales', point: this.getLatestPoint(stats, 'Per Cap Retail Sales'), sublabel: 'Latest consumer intensity' }
      ].filter((item) => item.point)
      : [
        { label: 'Population', point: this.getLatestPoint(stats, 'Total Population'), sublabel: 'Latest population scale' },
        { label: 'GDP', point: this.getLatestPoint(stats, 'GDP'), sublabel: 'Latest market output' },
        { label: 'Per Cap GDP', point: this.getLatestPoint(stats, 'Per Cap GDP'), sublabel: 'Latest output per person' },
        { label: 'Urban Population', point: this.getLatestPoint(stats, 'Urban Population'), sublabel: 'Latest urban base' }
      ].filter((item) => item.point);
    const spotlightMetrics = prefecture
      ? [
        { name: 'Total Population', yearData: stats && stats['Total Population'] },
        { name: 'Per Cap GDP', yearData: stats && stats['Per Cap GDP'] },
        { name: 'Per Cap Retail Sales', yearData: stats && stats['Per Cap Retail Sales'] }
      ].filter((metric) => metric.yearData)
      : [
        { name: 'Total Population', yearData: stats && stats['Total Population'] },
        { name: 'GDP', yearData: stats && stats['GDP'] },
        { name: 'Per Cap GDP', yearData: stats && stats['Per Cap GDP'] }
      ].filter((metric) => metric.yearData);
    const hasDistricts = prefecture && prefecture.relationships && prefecture.relationships.districts && prefecture.relationships.districts.data.length > 0;

    return (
      <div className="pref-analytics-shell">
        <div className="pref-analytics-head">
          <div>
            <div className="pref-analytics-kicker">Market Read</div>
            <div className="pref-analytics-title">{title}</div>
            <p className="pref-analytics-copy mb-0">Headline indicators and trend views for the selected market.</p>
          </div>
          <div className="pref-analytics-head-actions">
            {hasDistricts ? (
              <Button className="sl-location-cta sl-location-cta-prominent" size="sm" onClick={this.handleExploreClick.bind(this)}>
                Explore full profile
              </Button>
            ) : null}
            {stats ? (
              <HistoryControls
                historyWindow={historyWindow}
                onChange={(nextWindow) => this.setState({ historyWindow: nextWindow })}
                availableYears={availableYears} />
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className="text-center p-4">
            <FontAwesomeIcon icon={faSpinner} size="3x" spin className="text-primary" />
          </div>
        ) : stats ? (
          <>
            <div className="pref-kpi-grid">
              {overviewItems.map((item) => (
                <div className="pref-kpi-card pref-kpi-card-detailed" key={item.label}>
                  <div className="pref-kpi-label">{item.label}</div>
                  <div className="pref-kpi-value">{item.point.value}</div>
                  <div className="pref-kpi-sub">{item.sublabel} · {item.point.year}</div>
                </div>
              ))}
            </div>

            <Card className="pref-spotlight-card">
              <CardBody>
                <div className="pref-spotlight-head">
                  <div>
                    <div className="pref-spotlight-title">Trend Snapshot</div>
                    <p className="pref-spotlight-copy mb-0">Three high-signal metrics for {locationName} across the active time range.</p>
                  </div>
                  <span className="pref-spotlight-badge">{historyWindow.toUpperCase()}</span>
                </div>
                <div className="pref-spotlight-grid pref-spotlight-grid-wide">
                  {spotlightMetrics.map((metric) => (
                    <MetricCard
                      key={metric.name}
                      name={metric.name}
                      yearData={metric.yearData}
                      historyWindow={historyWindow}
                      compact={true} />
                  ))}
                </div>
              </CardBody>
            </Card>
          </>
        ) : (
          <div className="sl-empty-state mt-3">
            <div className="sl-empty-state-title">No market selected</div>
            <p className="sl-empty-state-copy mb-0">Select a province or prefecture to populate the market view.</p>
          </div>
        )}
      </div>
    );
  }

  renderDetailsShell(title, stats, loading, noteData) {
    const { historyWindow, province, prefecture, collapsedSections } = this.state;
    const demographicMetrics = this.getCategoryMetrics(stats, 'Demographic');
    const economicMetrics = this.getCategoryMetrics(stats, 'Economic');
    const consumptionMetrics = this.getCategoryMetrics(stats, 'Consumption');
    const retailMetrics = this.getCategoryMetrics(stats, 'Retail').concat(this.getCategoryMetrics(stats, 'Retailers'));
    const allMetrics = demographicMetrics.concat(economicMetrics, consumptionMetrics, retailMetrics);
    const availableYears = getMaxHistoryLength(allMetrics);

    if(!province && !prefecture) {
      return null;
    }

    return (
      <div className="sl-province-categories-shell">
        <div className="sl-province-categories-head">
          <div>
            <div className="pref-detail-kicker">Detailed Metrics</div>
            <div className="pref-detail-title">{title}</div>
          </div>
          {stats && allMetrics.length > 0 ? (
            <HistoryControls
              historyWindow={historyWindow}
              onChange={(nextWindow) => this.setState({ historyWindow: nextWindow })}
              availableYears={availableYears} />
          ) : null}
        </div>
        <div className="sl-province-sections-list">
          {this.renderFullWidthSection('Demographic', 'Demographics', 'Population structure, migration, workforce, and household composition.', faUsers, demographicMetrics, loading)}
          {this.renderFullWidthSection('Economic', 'Economics', 'Output, trade, sector mix, foreign investment, and income strength.', faYenSign, economicMetrics, loading)}
          {this.renderFullWidthSection('Retail', 'Retail', 'Retail demand, channel performance, and commercial market depth.', faStoreAlt, retailMetrics, loading)}
          {this.renderFullWidthSection('Consumption', 'Consumption & Wellness', 'Consumer assets, digital adoption, healthcare access, and daily living indicators.', faShoppingCart, consumptionMetrics, loading)}
          {noteData ? (
            <div className="data-section mb-3">
              <div className="data-section-header" onClick={() => this.toggleSection('Note')}>
                <div>
                  <div className="data-section-title">Note</div>
                  <div className="data-section-subtitle">Additional context captured for this market.</div>
                </div>
                <div className="data-section-header-right">
                  <span className="data-section-meta">Analyst context</span>
                  <FontAwesomeIcon icon={collapsedSections.Note ? faChevronDown : faChevronUp} className="text-muted" />
                </div>
              </div>
              <Collapse isOpen={!collapsedSections.Note}>
                <div className="p-3">
                  <div className="sl-note-title">{noteData.title}</div>
                  <p className="sl-note-body mb-0">{noteData.body}</p>
                </div>
              </Collapse>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  render() {
    const {
      prefecture,
      province,
      provinceStats,
      provinceStatsLoading,
      prefectureStats,
      prefectureStatsLoading,
    } = this.state;

    const currentScope = prefecture
      ? prefecture.attributes.name + ', ' + province.attributes.name
      : province
        ? province.attributes.name + ' province'
        : 'All China provinces';
    const shortlistLabel = province
      ? 'Shortlist inside ' + province.attributes.name
      : 'China-wide prefecture shortlist';
    const detailsTitle = prefecture
      ? prefecture.attributes.name + ' · Deep-dive by category'
      : province
        ? province.attributes.name + ' · Province metrics by category'
        : null;
    const detailsStats = prefecture ? prefectureStats : provinceStats;
    const detailsLoading = prefecture ? prefectureStatsLoading : provinceStatsLoading;
    const noteData = prefecture && prefecture.relationships && prefecture.relationships.note && prefecture.relationships.note.data
      ? prefecture.relationships.note.data
      : province && province.relationships && province.relationships.note && province.relationships.note.data
        ? province.relationships.note.data
        : null;
    const availableYears = detailsStats
      ? getMaxHistoryLength(Object.keys(detailsStats).map((key) => ({ yearData: detailsStats[key] })))
      : 0;
    const selectedMarketName = prefecture
      ? prefecture.attributes.name
      : province
        ? province.attributes.name
        : 'Explore China';

    return (
      <Container fluid={true} className="mt-3 pref-dashboard-shell">
        <div className="pref-page-hero">
          <div className="pref-page-header pref-page-header-tight">
            <div className="pref-page-title-block">
              <div className="pref-page-kicker">Data Exploration</div>
              <h1 className="pref-page-title">Explore Province and Prefecture Data</h1>
              <p className="pref-page-subtitle mb-0">Province and prefecture market analysis.</p>
            </div>
            <div className="pref-page-meta">
              <span className="pref-page-chip">{currentScope}</span>
            </div>
          </div>

          <div className="pref-page-hero-footer">
            <div className="pref-page-hero-copy">
              <div className="pref-page-hero-title">Interactive market workspace</div>
              <p className="pref-page-hero-text mb-0">Market overview, detailed metrics, and shortlist research in a single workspace.</p>
            </div>
            <div className="pref-page-hero-chips">
              <span className="pref-page-chip">{selectedMarketName}</span>
              <span className="pref-page-chip">{availableYears} years available</span>
            </div>
          </div>
        </div>
        <>
          <Row className="mb-4 align-items-stretch pref-workspace-row">
            <Col xl="5" lg="12">
              <Map
                province={province}
                prefecture={prefecture}
                updatePrefectures={this.updatePrefectures.bind(this)} />
            </Col>
            <Col xl="7" lg="12">
              {this.renderMarketRead()}
            </Col>
          </Row>

          {this.renderDetailsShell(
            prefecture ? prefecture.attributes.name + ' detailed metrics' : province ? province.attributes.name + ' detailed metrics' : 'Detailed metrics',
            prefecture ? prefectureStats : provinceStats,
            prefecture ? prefectureStatsLoading : provinceStatsLoading,
            prefecture && prefecture.relationships && prefecture.relationships.note ? prefecture.relationships.note.data : null
          )}
        </>

        <div className="sl-explorer-shortlist-shell">
          <div className="sl-explorer-shortlist-head">
            <div>
              <div className="pref-detail-kicker">Research Shortlist</div>
              <div className="pref-detail-title">Prefecture shortlist</div>
              <p className="sl-explorer-shortlist-copy">Browse and open additional prefectures from the current scope.</p>
            </div>
            <div className="sl-explorer-shortlist-badge">{shortlistLabel}</div>
          </div>
          <Search
            province={province}
            updatePrefectures={this.updatePrefectures.bind(this)} />
        </div>
      </Container>
    );
  }
}

export default Prefectures;
